import { useState } from 'react'
import { AlertCircle, Copy, CheckCircle2, Eye, EyeOff, Shield, X, Mail } from 'lucide-react'

interface CredentialModalProps {
  credentials: { email: string; password: string; full_name?: string } | null
  emailQueued?: boolean
  isReset?: boolean
  onDismiss: () => void
}

const CredentialModal: React.FC<CredentialModalProps> = ({
  credentials, emailQueued, isReset, onDismiss
}) => {
  const [passwordVisible, setPasswordVisible] = useState(true)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  if (!credentials) return null

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const handleDismiss = () => {
    onDismiss()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {isReset ? 'Credentials Reset' : 'Admin Credentials'}
            </h3>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Security warning banner */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Save these credentials now</p>
                <p className="mt-1">The password will not be shown again after this dialog is closed.</p>
              </div>
            </div>
          </div>

          {/* Email status notification */}
          {emailQueued && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm text-blue-800">Credentials have been emailed to the operator</span>
            </div>
          )}

          {/* Credential fields */}
          <div className="space-y-3">
            {/* Email field */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono">
                  {credentials.email}
                </code>
                <button
                  onClick={() => copyToClipboard(credentials.email, 'email')}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title="Copy email"
                >
                  {copiedField === 'email' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Password field */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Password</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono">
                  {passwordVisible ? credentials.password : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                </code>
                <button
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title={passwordVisible ? 'Hide password' : 'Show password'}
                >
                  {passwordVisible ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => copyToClipboard(credentials.password, 'password')}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title="Copy password"
                >
                  {copiedField === 'password' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Done button */}
          <button
            onClick={handleDismiss}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

export default CredentialModal
